/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { FeatureFlag, getFeatureFlag, hasPermission, type UserRole } from '@repo/shared-common'

export const IMAGE_EDITOR_FREE_USES_EXHAUSTED_CODE = 'IMAGE_EDITOR_FREE_USES_EXHAUSTED'
export const IMAGE_EDITOR_FREE_USES_EXHAUSTED_MESSAGE =
  'You have used your free image editor saves. Become a supporter for unlimited edits.'

export type ImageEditorUsageState = {
  usedCount: number
  freeUses: number
  remainingUses: number
  unlimited: boolean
}

export const hasUnlimitedImageEditorAccess = (role: UserRole | undefined) => hasPermission(role, 'media:edit-image')

export const imageEditorFreeUsesForRole = (role: UserRole | undefined) => {
  const freeUses = getFeatureFlag(role, FeatureFlag.IMAGE_EDITOR_FREE_USES)
  if (!Number.isFinite(freeUses)) {
    return 0
  }
  return Math.max(0, Math.floor(freeUses))
}

export const buildImageEditorUsageState = ({
  usedCount,
  role,
}: {
  usedCount: number
  role: UserRole | undefined
}): ImageEditorUsageState => {
  const unlimited = hasUnlimitedImageEditorAccess(role)
  const freeUses = imageEditorFreeUsesForRole(role)
  const safeUsedCount = Math.max(0, Math.floor(usedCount))

  return {
    usedCount: safeUsedCount,
    freeUses,
    remainingUses: unlimited ? 0 : Math.max(0, freeUses - safeUsedCount),
    unlimited,
  }
}

export const canApplyImageEditorEdits = (usage: ImageEditorUsageState | null | undefined) =>
  Boolean(usage && (usage.unlimited || usage.remainingUses > 0))

export const exhaustedImageEditorUsageState = (
  usage?: Pick<ImageEditorUsageState, 'usedCount' | 'freeUses'> | null
): ImageEditorUsageState => {
  const freeUses = Math.max(0, Math.floor(usage?.freeUses ?? 0))
  const usedCount = Math.max(freeUses, Math.max(0, Math.floor(usage?.usedCount ?? 0)))

  return {
    usedCount,
    freeUses,
    remainingUses: 0,
    unlimited: false,
  }
}
